import React from 'react';
import { useParams, Link, Switch, Route, useRouteMatch, useLocation, Redirect } from 'react-router-dom';
import { FaChevronLeft } from 'react-icons/fa';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';
import Loading from '../../components/Loading';

import Metadata, { contributorsList, getImageUrl } from './subSections/Metadata';
import ProductSummary from './subSections/ProductSummary';
import ProductHistory from './subSections/ProductHistory';
import Layout from '../../components/Layout';
import SEO from '../../components/seo';
import Audio from './subSections/Audio';
import PushDistributionView from './subSections/PushDistribution';
import { GET_SPOTIFY_URI } from '../../queries';
import CopyText from '../../components/CopyText';
import SpotifyOpenLink from '../../components/SpotifyOpenLink';

const GET_CUBE_DETAIL = gql`
    query cubeByContentId($id: String!, $platformName: AudioEnginePlatform!) {
        cubeByContentId(contentId: $id) {
            results {
                _created
                _modified
                abridgement
                active
                actualDuration
                aggregator
                awards
                bisacCodes
                contentId
                contributors {
                    authors {
                        fullName
                    }
                    narrators {
                        fullName
                    }
                }
                description
                genre
                fictionType
                language
                keywords
                netsuiteId
                originalReleaseDate
                publisherDuration
                publisherName
                publisherDisplayName
                publisherId
                secondaryGenre
                seriesNumber
                seriesTitle
                sku
                subtitle
                title
                modelRights {
                    library {
                        isbn
                        listPrice
                        listPriceCurrency
                        territoriesExcluded
                        territoriesExclusive
                        territoriesIncluded
                    }
                    retail {
                        isbn
                        listPrice
                        listPriceCurrency
                        territoriesExcluded
                        territoriesExclusive
                        territoriesIncluded
                    }
                }
                audioEngineBook(platformName: $platformName) {
                    audiobook {
                        sample_url
                        id
                        chapterized
                        grade_level
                        runtime
                        chapters {
                            chapter_number
                        }
                        supplements {
                            url
                        }
                    }
                }
                audioCohort {
                    fileList {
                        metadata {
                            duration
                        }
                    }
                }
            }
        }
    }
`;

export const useQueryString = (search) => {
    return new URLSearchParams(search);
};

const AudiobookDetail = () => {
    const { path, url } = useRouteMatch();
    let { contentId } = useParams();
    let location = useLocation();

    const { loading, data, error } = useQuery(GET_CUBE_DETAIL, {
        variables: { id: contentId, platformName: 'platform_spotify' }
    });
    const { data: uriData } = useQuery(GET_SPOTIFY_URI, { variables: { contentId }, skip: !contentId });

    const book = data?.cubeByContentId?.results[0];

    return (
        <Layout useContainer={false}>
            {loading && (
                <div className="p-4">
                    <Loading />
                </div>
            )}
            {!loading && error && (
                <div className="container py-6">
                    <Link to={'/'}>
                        <FaChevronLeft className="pt-1" />
                        Back to Home
                    </Link>
                    <div className="flash border-danger txt-danger mt-6">
                        <p>
                            <strong>Message: </strong>
                            <code>{error?.message}</code>
                        </p>
                    </div>
                </div>
            )}
            {!loading && !error && book && (
                <>
                    <SEO
                        type="book"
                        title={book?.title}
                        description={book?.description}
                        image={getImageUrl(book?.sku)}
                        meta={[{ property: 'og:book:author', content: contributorsList(book?.contributors?.authors) }]}
                    />
                    <div className="mb-12">
                        {location.pathname !== `/title-management/${contentId}` && (
                            <div className="bg-light p-4 pos-sticky border-b border-muted">
                                <div className="container">
                                    <div className="row">
                                        <div className="col">
                                            <img
                                                className="d-block rounded img-center"
                                                alt="Cover"
                                                src={getImageUrl(book.sku, 100)}
                                                srcSet={`${getImageUrl(book.sku, 200)} 2x`}
                                            />
                                        </div>
                                        <div className="col-xs-12 col-md-6">
                                            <h2 className="mb-2 h4 txt-truncate">{book?.title}</h2>
                                            <ul className="list-flat">
                                                <li>
                                                    <strong>Content ID:</strong> {book?.contentId}
                                                    <CopyText text={book?.contentId} title="Copy Content ID" />
                                                </li>
                                                {!!uriData?.findagatewayMapping && (
                                                    <li>
                                                        <strong>Spotify URI:</strong>{' '}
                                                        <SpotifyOpenLink uri={uriData.findagatewayMapping} />
                                                        <CopyText
                                                            text={uriData.findagatewayMapping}
                                                            title="Copy Spotify URI"
                                                        />
                                                    </li>
                                                )}
                                            </ul>
                                        </div>
                                        <div className="col-xs-12 col-md-4">
                                            <ul className="list-flat">
                                                <li>
                                                    <strong>Library ISBN:</strong>{' '}
                                                    {book.modelRights?.library?.[0].isbn
                                                        ? book.modelRights?.library?.[0].isbn
                                                        : '--'}
                                                </li>
                                                <li>
                                                    <strong>Retail ISBN:</strong>{' '}
                                                    {book.modelRights?.retail?.[0].isbn
                                                        ? book.modelRights?.retail?.[0].isbn
                                                        : '--'}
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="container mx-auto mt-4">
                            <Link to={'/'}>
                                <FaChevronLeft className="pt-1" />
                                Back to Home
                            </Link>
                            <Switch>
                                <Route exact path={path}>
                                    <Metadata book={book} />
                                </Route>
                                <Route path={`${path}/audio`}>
                                    <Audio
                                        contentId={book?.contentId}
                                        chapterized={book.chapterized}
                                        sampleUrl={book.audioEngineBook.audiobook.sample_url}
                                    />
                                </Route>
                                <Route path={`${path}/product-summary`}>
                                    <ProductSummary contentId={book?.contentId} />
                                </Route>
                                <Route path={`${path}/product-history/:platform?`}>
                                    <ProductHistory contentId={book?.contentId} bisacCodes={book.bisacCodes} />
                                </Route>
                                <Route path={`${path}/transactional-detail`}>
                                    <div className="main-wrapper">
                                        <section>
                                            <h4>No Longer Available</h4>
                                            <p>
                                                Please use either{' '}
                                                <a
                                                    href={`https://fortknox.inaudio.com/transactions?audiobook_id=${book?.contentId}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    FortKnox
                                                </a>{' '}
                                                or PowerBI for sales insights.
                                            </p>
                                        </section>
                                    </div>
                                </Route>
                                <Route path={`${path}/push-history`}>
                                    <Redirect to={url} />
                                </Route>
                                <Route path={`${path}/push-distribution`}>
                                    <PushDistributionView contentId={book?.contentId} />
                                </Route>
                                <Route>
                                    <div className="main-wrapper">
                                        <section>
                                            <h2 className="mb-12">AudioEngine Gateway</h2>
                                            <h4>Page Not Found</h4>
                                            <p>Return to the home screen and access the left sidebar navigation.</p>
                                        </section>
                                    </div>
                                </Route>
                            </Switch>
                        </div>
                    </div>
                </>
            )}
        </Layout>
    );
};

export default AudiobookDetail;
